import React, { useState } from 'react';
import config from './config';

const PatentabilityCheckPage = ({ user, onBack, setPage, onUsageUpdate }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ percent: 0, message: '' });
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    novelty: '',
    field: ''
  });
  
  // Results
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [suggestingField, setSuggestingField] = useState(false);
  const [suggestedFields, setSuggestedFields] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Suggest technical fields using AI
  const suggestFields = async () => {
    if (!formData.title && !formData.description) {
      setError('Please enter a title or description first');
      return;
    }

    setSuggestingField(true);
    setSuggestedFields([]);

    try {
      const response = await fetch(`${config.API_URL}/api/suggest-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description
        })
      });

      const data = await response.json();
      if (data.success && data.fields) {
        setSuggestedFields(data.fields);
      }
    } catch (err) {
      console.error('Error suggesting fields:', err);
    } finally {
      setSuggestingField(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim() || !formData.novelty.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setStep(2);
    setProgress({ percent: 10, message: 'Analyzing your invention...' });

    try {
      const token = localStorage.getItem('token');
      
      // Simulate progress updates
      const progressUpdates = [
        { percent: 20, message: 'Identifying key technical features...' },
        { percent: 40, message: 'Searching patent database...' },
        { percent: 60, message: 'Analyzing similar patents...' },
        { percent: 80, message: 'Evaluating novelty...' },
        { percent: 90, message: 'Generating assessment...' }
      ];
      
      let progressIndex = 0;
      const progressInterval = setInterval(() => {
        if (progressIndex < progressUpdates.length) {
          setProgress(progressUpdates[progressIndex]);
          progressIndex++;
        }
      }, 1500);

      const response = await fetch(`${config.API_URL}/api/patentability-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          novelty: formData.novelty,
          field: formData.field,
          token: token
        })
      });

      clearInterval(progressInterval);
      
      const data = await response.json();
      
      if (data.success) {
        setResults(data);
        setStep(3);
        if (onUsageUpdate) onUsageUpdate();
      } else {
        setError(data.error || 'Error analyzing patentability');
        setStep(1);
      }
    } catch (err) {
      setError('Error connecting to server. Please try again.');
      setStep(1);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetJourney = () => {
    setStep(1);
    setFormData({ title: '', description: '', novelty: '', field: '' });
    setResults(null);
    setError('');
  };

  const getSignalColor = (signal) => {
    switch (signal) {
      case 'GREEN': return { bg: '#dcfce7', border: '#16a34a', text: '#166534', icon: '🟢' };
      case 'CAUTION': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', icon: '🟡' };
      case 'RED': return { bg: '#fef2f2', border: '#dc2626', text: '#dc2626', icon: '🔴' };
      default: return { bg: '#f1f5f9', border: '#64748b', text: '#475569', icon: '⚪' };
    }
  };

  // Save report to database
  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save reports');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/api/saved-individual-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: 'patentability-check',
          title: formData?.title || 'Patentability Check Report',
          input_data: formData,
          results_data: results,
          token: token
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (err) {
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  // Download as HTML
  const downloadHTML = () => {
    const signalColors = {
      GREEN: { bg: '#dcfce7', border: '#16a34a', text: '#166534' },
      CAUTION: { bg: '#fef3c7', border: '#d97706', text: '#92400e' },
      RED: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' }
    };
    const signal = results?.signal || 'CAUTION';
    const colors = signalColors[signal] || signalColors.CAUTION;

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Patentability Check: ${formData?.title || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; line-height: 1.6; }
    h1 { color: #1e293b; border-bottom: 3px solid #0369a1; padding-bottom: 10px; }
    h2 { color: #0369a1; margin-top: 30px; }
    .signal-box { background: ${colors.bg}; border-left: 4px solid ${colors.border}; padding: 20px; margin: 20px 0; border-radius: 8px; }
    .signal-label { font-size: 24px; font-weight: bold; color: ${colors.text}; }
    .signal-explanation { color: ${colors.text}; margin-top: 10px; }
    .summary-box { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .invention-details { background: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .invention-details p { margin: 10px 0; }
    .invention-details strong { color: #0369a1; }
    .patent-card { background: white; border: 1px solid #e2e8f0; padding: 15px; border-radius: 8px; margin: 10px 0; }
    .patent-card h4 { margin: 0 0 10px 0; color: #1e293b; }
    .similarity-high { color: #dc2626; font-weight: bold; }
    .similarity-medium { color: #d97706; font-weight: bold; }
    .similarity-low { color: #16a34a; font-weight: bold; }
    .list-section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .list-section ul { margin: 10px 0; padding-left: 25px; }
    .list-section li { margin: 8px 0; }
    .novelty-item { color: #166534; }
    .concern-item { color: #dc2626; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Patentability Check Report</h1>

  <div class="signal-box">
    <div class="signal-label">${signal === 'GREEN' ? '🟢 Proceed with Confidence' : signal === 'RED' ? '🔴 Significant Concerns' : '🟡 Proceed with Caution'}</div>
    <div class="signal-explanation">${results?.signal_explanation || ''}</div>
  </div>

  <h2>Invention Details</h2>
  <div class="invention-details">
    <p><strong>Title:</strong> ${formData?.title || 'Not specified'}</p>
    <p><strong>Description:</strong> ${formData?.description || 'Not specified'}</p>
    <p><strong>Claimed Novelty:</strong> ${formData?.novelty || 'Not specified'}</p>
    <p><strong>Technical Field:</strong> ${formData?.field || 'Not specified'}</p>
  </div>

  <h2>Assessment Summary</h2>
  <div class="summary-box">
    <p>${results?.summary || 'No summary available'}</p>
  </div>

  <h2>Potential Novelty Points</h2>
  <div class="list-section">
    <ul>
      ${(results?.novelty_points || []).map(point => `<li class="novelty-item">✓ ${point}</li>`).join('') || '<li>No novelty points identified</li>'}
    </ul>
  </div>

  <h2>Potential Concerns</h2>
  <div class="list-section">
    <ul>
      ${(results?.concerns || []).map(concern => `<li class="concern-item">⚠ ${concern}</li>`).join('') || '<li>No significant concerns identified</li>'}
    </ul>
  </div>

  ${results?.similar_patents && results.similar_patents.length > 0 ? `
    <h2>Similar Patents Found (${results.total_similar_found || results.similar_patents.length})</h2>
    ${results.similar_patents.map(p => `
      <div class="patent-card">
        <h4>${p.patent_number}: ${p.title}</h4>
        <p><strong>Similarity:</strong> <span class="similarity-${(p.similarity || 'medium').toLowerCase()}">${p.similarity || 'Unknown'}</span></p>
        <p>${p.relevance_note || ''}</p>
      </div>
    `).join('')}
  ` : '<h2>Similar Patents</h2><p>No highly similar patents found in initial search.</p>'}

  <h2>Recommended Next Steps</h2>
  <div class="list-section">
    <ol>
      ${(results?.next_steps || []).map(step => `<li>${step}</li>`).join('') || '<li>Consult with a patent attorney</li>'}
    </ol>
  </div>

  <div class="footer">
    <p><strong>Important:</strong> This is an automated preliminary assessment, not legal advice. A professional patent search and attorney review are essential before filing.</p>
    <p>Generated by PatTrak - ${new Date().toLocaleDateString()}</p>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PatTrak_Patentability_${(formData?.title || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download as PDF
  const downloadPDF = async () => {
    if (!results) return;
    const signal = results?.signal || 'CAUTION';
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Patentability Check: ${formData?.title || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
    h1 { color: #1e293b; font-size: 24px; }
    h2 { color: #0369a1; font-size: 18px; border-bottom: 2px solid #0369a1; padding-bottom: 8px; margin-top: 30px; }
    .section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .signal-box { padding: 15px; border-radius: 8px; margin: 15px 0; }
    .signal-green { background: #dcfce7; border-left: 4px solid #16a34a; }
    .signal-caution { background: #fef3c7; border-left: 4px solid #d97706; }
    .signal-red { background: #fee2e2; border-left: 4px solid #dc2626; }
    ul, ol { margin: 10px 0; padding-left: 20px; }
    li { margin: 8px 0; }
    .novelty { background: #dcfce7; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .novelty li { color: #166534; }
    .concerns { background: #fee2e2; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .concerns li { color: #991b1b; }
    .patent-card { background: white; border: 1px solid #e2e8f0; padding: 12px; margin: 10px 0; border-radius: 6px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Patentability Check Report</h1>
  <p><strong>Invention:</strong> ${formData?.title || 'Not specified'}</p>
  <p><strong>Technical Field:</strong> ${formData?.field || 'Not specified'}</p>
  <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>

  <div class="signal-box signal-${signal.toLowerCase()}">
    <strong>${signal === 'GREEN' ? '🟢 Proceed with Confidence' : signal === 'RED' ? '🔴 Significant Concerns' : '🟡 Proceed with Caution'}</strong>
    <p>${results?.signal_explanation || ''}</p>
  </div>

  <h2>Assessment Summary</h2>
  <div class="section">${results?.summary || ''}</div>

  <h2>Potential Novelty Points</h2>
  <div class="novelty"><ul>${(results?.novelty_points || []).map(item => '<li>✓ ' + item + '</li>').join('') || '<li>None identified</li>'}</ul></div>

  <h2>Potential Concerns</h2>
  <div class="concerns"><ul>${(results?.concerns || []).map(item => '<li>⚠ ' + item + '</li>').join('') || '<li>None identified</li>'}</ul></div>

  ${results?.similar_patents && results.similar_patents.length > 0 ? '<h2>Similar Patents Found (' + results.similar_patents.length + ')</h2>' + results.similar_patents.map(p => '<div class="patent-card"><strong>' + p.patent_number + ':</strong> ' + p.title + '<br/><small>Similarity: ' + p.similarity + '</small><p>' + (p.relevance_note || '') + '</p></div>').join('') : ''}

  <h2>Recommended Next Steps</h2>
  <div class="section"><ol>${(results?.next_steps || []).map(item => '<li>' + item + '</li>').join('') || '<li>Consult patent attorney</li>'}</ol></div>

  <div class="footer">Generated by PatTrak - Patent Intelligence Made Simple<br/>This is not legal advice.</div>
</body>
</html>`;
    try {
      const response = await fetch(`${config.API_URL}/api/generate-invention-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_html: htmlContent,
          title: formData?.title || 'Patentability'
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PatTrak_Patentability_${(formData?.title || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF error:', error);
      alert('Failed to generate PDF');
    }
  };

  // Tier gate - only Premium and Pro users can access
  if (!user || user.tier === 'free') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
            ← Back
          </button>
        </header>
        
        <main style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔒</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              Patentability Check
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Assess whether your invention is likely patentable based on novelty and prior art.
            </p>
            
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '20px', marginBottom: '32px' }}>
              <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
                {!user 
                  ? 'Please register or log in to access this tool.'
                  : 'This tool requires a Premium or Pro subscription.'
                }
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '280px', margin: '0 auto' }}>
              {!user ? (
                <>
                  <button onClick={() => setPage('register')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Register Free
                  </button>
                  <button onClick={() => setPage('login')} style={{ padding: '14px 28px', background: 'white', color: '#0369a1', border: '1px solid #0369a1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Log In
                  </button>
                </>
              ) : (
                <button onClick={() => setPage('pricing')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                  Upgrade to Premium
                </button>
              )}
              <button onClick={onBack} style={{ padding: '10px', background: 'none', color: '#64748b', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                Maybe Later
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#0369a1',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: 0,
            marginBottom: '16px'
          }}
        >
          ← Back to Search
        </button>
        
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          ✓ Patentability Check
        </h1>
        <p style={{ color: '#64748b', marginTop: '8px', fontSize: '16px' }}>
          Get a quick signal on whether your invention might be patentable
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '32px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        {[
          { num: 1, label: 'Describe Invention' },
          { num: 2, label: 'Searching' },
          { num: 3, label: 'Results' }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              opacity: step >= s.num ? 1 : 0.4
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: step > s.num ? '#16a34a' : step === s.num ? '#0369a1' : '#e2e8f0',
                color: step >= s.num ? 'white' : '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: step === s.num ? '600' : '400',
                color: step === s.num ? '#0369a1' : '#64748b'
              }}>
                {s.label}
              </span>
            </div>
            {idx < 2 && (
              <div style={{ 
                flex: 1, 
                height: '2px', 
                background: step > s.num ? '#16a34a' : '#e2e8f0',
                margin: '0 16px'
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Describe Invention */}
      {step === 1 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '32px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Tell me about your invention
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            I'll search for similar patents and give you a quick patentability signal.
          </p>

          {/* Invention Title */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Invention Title <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Self-healing concrete with embedded microcapsules"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              What does it do? <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your invention in 2-3 sentences. What problem does it solve? How does it work?"
              rows={4}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                resize: 'vertical'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Novelty */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              What's new about it? <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={formData.novelty}
              onChange={(e) => handleInputChange('novelty', e.target.value)}
              placeholder="What makes your invention different from what already exists? What's the key innovation?"
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                resize: 'vertical'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Technical Field (optional) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Technical Field <span style={{ color: '#94a3b8' }}>(optional - helps find relevant patents)</span>
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={formData.field}
                onChange={(e) => handleInputChange('field', e.target.value)}
                placeholder="e.g., construction materials, biotechnology, software"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  fontSize: '15px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#0369a1'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <button
                type="button"
                onClick={suggestFields}
                disabled={suggestingField || (!formData.title && !formData.description)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #0369a1',
                  background: suggestingField ? '#e0f2fe' : 'white',
                  color: '#0369a1',
                  cursor: suggestingField || (!formData.title && !formData.description) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                {suggestingField ? 'Thinking...' : 'Suggest'}
              </button>
            </div>
            {suggestedFields.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {suggestedFields.map((field, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => { handleInputChange('field', field); setSuggestedFields([]); }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '16px',
                      border: '1px solid #0369a1',
                      background: '#f0f9ff',
                      color: '#0369a1',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {field}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!formData.title.trim() || !formData.description.trim() || !formData.novelty.trim()}
            style={{
              width: '100%',
              padding: '14px 24px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              background: (!formData.title.trim() || !formData.description.trim() || !formData.novelty.trim()) 
                ? '#94a3b8' : '#0369a1',
              border: 'none',
              borderRadius: '8px',
              cursor: (!formData.title.trim() || !formData.description.trim() || !formData.novelty.trim()) 
                ? 'not-allowed' : 'pointer'
            }}
          >
            Check Patentability →
          </button>

          {/* Learning moment */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #bbf7d0'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
              💡 What makes an invention patentable?
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
              <li><strong>Novel</strong> - Not already known or published</li>
              <li><strong>Non-obvious</strong> - Not an obvious combination of existing things</li>
              <li><strong>Useful</strong> - Has a practical application</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 2: Searching */}
      {step === 2 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '48px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Searching Patent Database...
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            {progress.message}
          </p>
          
          {/* Progress bar */}
          <div style={{
            width: '300px',
            height: '8px',
            background: '#e2e8f0',
            borderRadius: '4px',
            margin: '0 auto 16px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress.percent}%`,
              height: '100%',
              background: '#0369a1',
              borderRadius: '4px',
              transition: 'width 0.5s ease'
            }} />
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {progress.percent}% complete
          </div>

          {/* What's happening */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            textAlign: 'left'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              What's happening:
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#64748b' }}>
              <li>Extracting key technical features from your description</li>
              <li>Searching 1.45 million USPTO patents</li>
              <li>Identifying similar inventions</li>
              <li>Analyzing potential novelty issues</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div>
          {/* Signal Banner */}
          {(() => {
            const signal = getSignalColor(results.signal);
            return (
              <div style={{
                background: signal.bg,
                border: `2px solid ${signal.border}`,
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>{signal.icon}</div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: signal.text, margin: 0 }}>
                  {results.signal === 'GREEN' && 'Looks Promising!'}
                  {results.signal === 'CAUTION' && 'Proceed with Caution'}
                  {results.signal === 'RED' && 'Significant Concerns'}
                </h2>
                <p style={{ color: signal.text, marginTop: '8px', fontSize: '16px' }}>
                  {results.signal_explanation}
                </p>
              </div>
            );
          })()}

          {/* Summary */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
              📋 Assessment Summary
            </h3>
            <p style={{ fontSize: '15px', color: '#475569', lineHeight: '1.6', margin: 0 }}>
              {results.summary}
            </p>
          </div>

          {/* Similar Patents Found */}
          {results.similar_patents && results.similar_patents.length > 0 && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                🔎 Similar Patents Found ({results.similar_patents.length})
              </h3>
              {results.similar_patents.map((patent, idx) => (
                <div key={idx} style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  marginBottom: idx < results.similar_patents.length - 1 ? '12px' : 0
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                        {patent.patent_number}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b', marginBottom: '8px' }}>
                        {patent.title}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        {patent.relevance_note}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      background: patent.similarity === 'High' ? '#fef2f2' : 
                                 patent.similarity === 'Medium' ? '#fef3c7' : '#f0fdf4',
                      color: patent.similarity === 'High' ? '#dc2626' : 
                             patent.similarity === 'Medium' ? '#92400e' : '#166534',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {patent.similarity} similarity
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* What Makes Yours Different */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#166534', marginBottom: '12px' }}>
                ✓ Potential Novelty Points
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#15803d', fontSize: '14px', lineHeight: '1.8' }}>
                {results.novelty_points?.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            </div>

            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626', marginBottom: '12px' }}>
                ⚠️ Potential Concerns
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#b91c1c', fontSize: '14px', lineHeight: '1.8' }}>
                {results.concerns?.map((concern, idx) => (
                  <li key={idx}>{concern}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Recommended Next Steps */}
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1', marginBottom: '12px' }}>
              📝 Recommended Next Steps
            </h3>
            <ol style={{ margin: 0, paddingLeft: '24px', color: '#0c4a6e', fontSize: '14px', lineHeight: '2' }}>
              {results.next_steps?.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Disclaimer */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              <strong>⚠️ Important:</strong> This is an automated preliminary assessment, not legal advice. 
              A professional patent search and attorney review are essential before filing. This check covers 
              USPTO patents only and may not reflect international prior art or non-patent publications.
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={resetJourney}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#0369a1',
                background: 'white',
                border: '2px solid #0369a1',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Check Another Invention
            </button>
            <button
              onClick={saveReport}
              disabled={saving}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: saveSuccess ? '#16a34a' : '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Report'}
            </button>
            <button
              onClick={downloadHTML}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download HTML
            </button>
            <button
              onClick={downloadPDF}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download PDF
            </button>
          </div>

          {/* What You Learned */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              📚 What you learned
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#64748b' }}>
              <li>How to identify key novelty points in an invention</li>
              <li>What prior art might challenge patentability</li>
              <li>The importance of professional patent search before filing</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatentabilityCheckPage;